import InvestorContactCard from 'AdminModelCard';
import BackendService from 'BackendService';
import AdminInvestorExtraSection from 'FundUpdate/AdminInvestorExtraSection';
import { Box } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RecentlyViewedSidebar from 'RecentlyViewedSidebar';
import ContentEditorToolbar from '../../components/admin/newContentManagement/ContentEditorToolbar';
import CashflowReportsSection from '../../components/fundUpdatesAdmin/CashflowReportsSection';
import FundUpdatePagePreview from '../../components/fundUpdatesAdmin/FundUpdatePagePreview';
import { usePageLevelPreviewMode } from '../../components/fundUpdatesAdmin/usePageLevelPreviewMode';
import FundInformation from '../../components/FundUpdate/FundInformation';
import SimplifiedFundUpdatesAdminPanel, {
  FundManagementToolbarState,
} from '../../components/fundUpdatesAdmin/SimplifiedFundUpdatesAdminPanel';
import StyledModal from '../../components/styles/StyledModal';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../../components/styles/StyledButtons';
import { StyledHeading } from '../../components/styles/styles';
import type { PageMode } from '../../components/admin/newContentManagement/newContentManagementEditorTypes';
import FundOpportunity from '../../models/FundOpportunity';
import Investor, { investorToContactCardProps } from '../../models/Investor';
import FundUpdateBase from '../FundUpdateBase';

interface FundUpdateProps {
  fundOpportunity: FundOpportunity;
  investor?: Investor;
}

interface AdminEditPanelProps {
  fundOpportunity: FundOpportunity;
  fundOpportunityId: string;
  fundName: string;
  onToolbarWidthChange?: (width: number) => void;
  onToolbarStateChange?: (
    state:
      | FundManagementToolbarState
      | null
      | ((prev: FundManagementToolbarState | null) => FundManagementToolbarState | null),
  ) => void;
}

function AdminEditPanel(props: AdminEditPanelProps) {
  const {
    fundOpportunity,
    fundOpportunityId,
    fundName,
    onToolbarWidthChange,
    onToolbarStateChange,
  } = props;
  return (
    <SimplifiedFundUpdatesAdminPanel
      fundOpportunity={fundOpportunity}
      fundOpportunityId={fundOpportunityId}
      fundName={fundName}
      onToolbarWidthChange={onToolbarWidthChange}
      onToolbarStateChange={onToolbarStateChange}
    />
  );
}

function AdminPrependSection({
  investor,
  fundOpportunity,
  onDocumentsAdded,
  onInvestorUpdate,
}: {
  investor?: Investor;
  fundOpportunity: FundOpportunity;
  onDocumentsAdded: () => void;
  onInvestorUpdate: (investor: Investor) => void;
}) {
  if (!investor) {
    return (
      <Box sx={{ mb: 3 }}>
        <AdminInvestorExtraSection
          fundOpportunity={fundOpportunity}
          onDocumentsAdded={onDocumentsAdded}
        />
      </Box>
    );
  }
  return (
    <Box sx={{ mb: 3 }}>
      <RecentlyViewedSidebar modelType="INVESTOR" limit={5} compact={true} />
      <StyledHeading headerVariant="subheading">
        Viewing for
        <InvestorContactCard {...investorToContactCardProps(investor, 'tone1')} />
      </StyledHeading>
      <AdminInvestorExtraSection
        investor={investor}
        fundOpportunity={fundOpportunity}
        onDocumentsAdded={onDocumentsAdded}
        onInvestorUpdate={onInvestorUpdate}
      />
    </Box>
  );
}

function AdminAppendSection({
  investor,
  fundOpportunity,
}: {
  investor?: Investor;
  fundOpportunity: FundOpportunity;
}) {
  if (!investor) return null;
  return (
    <FundInformation
      investor={investor}
      fundOpportunity={fundOpportunity}
      loadDocs={() => new BackendService().getInvestorDocuments(investor.id)}
    />
  );
}

type BulkConfirm = 'publish' | 'revert' | null;

function getBulkToolbarActions(
  state: FundManagementToolbarState | null,
  onBulkConfirm: (action: BulkConfirm) => void,
  previewing: boolean,
) {
  // The update list isn't mounted during a page-level preview, so bulk actions wait for Edit.
  // Until the list reports in, both stay disabled.
  const bulk: Partial<FundManagementToolbarState> = state ?? {};
  return {
    onPublishAll: bulk.onPublishAll ? () => onBulkConfirm('publish') : undefined,
    publishAllDisabled: previewing || bulk.publishAllDisabled !== false,
    publishingAll: bulk.publishingAll,
    onRevertAll: bulk.onRevertAll ? () => onBulkConfirm('revert') : undefined,
    revertAllDisabled: previewing || bulk.revertAllDisabled !== false,
    revertingAll: bulk.revertingAll,
  };
}

function FundManagementToolbar({
  fundOpportunity,
  toolbarState,
  fallbackToolbarRef,
  onBulkConfirm,
  pageMode,
  onPageModeChange,
  pageLevelPreview,
}: {
  fundOpportunity: FundOpportunity;
  toolbarState: FundManagementToolbarState | null;
  fallbackToolbarRef: React.RefObject<HTMLDivElement | null>;
  onBulkConfirm: (action: BulkConfirm) => void;
  pageMode: PageMode;
  onPageModeChange: (mode: PageMode) => void;
  pageLevelPreview: boolean;
}) {
  const navigate = useNavigate();
  return (
    <ContentEditorToolbar
      fundOpportunity={fundOpportunity}
      titlePrefix="Fund Management"
      saveStatus={toolbarState?.saveStatus ?? 'saved'}
      pageMode={pageMode}
      toolbarRef={toolbarState?.toolbarRef ?? fallbackToolbarRef}
      onBack={() => navigate(`/opportunities/${fundOpportunity.id}`)}
      onPageModeChange={onPageModeChange}
      {...getBulkToolbarActions(toolbarState, onBulkConfirm, pageLevelPreview)}
    />
  );
}

/** The page-level preview while one is showing, otherwise the editable fund update page. */
function FundManagementBody({
  view,
  fundOpportunity,
  children,
}: {
  view: ReturnType<typeof usePageLevelPreviewMode>;
  fundOpportunity: FundOpportunity;
  children: React.ReactNode;
}) {
  if (!view.isPageLevelPreview || view.pageMode === 'edit') return <>{children}</>;
  return (
    <FundUpdatePagePreview
      fundOpportunity={fundOpportunity}
      pageMode={view.pageMode}
      generation={view.generation}
    />
  );
}

function BulkConfirmModal({
  action,
  toolbarState,
  onClose,
}: {
  action: BulkConfirm;
  toolbarState: FundManagementToolbarState | null;
  onClose: () => void;
}) {
  const confirm = () => {
    if (action === 'publish') toolbarState?.onPublishAll?.();
    else toolbarState?.onRevertAll?.();
    onClose();
  };
  return (
    <StyledModal
      open={action !== null}
      onClose={onClose}
      title={action === 'publish' ? 'Publish All Updates' : 'Revert To Published'}
      subtitle={
        action === 'publish'
          ? 'This will publish every unpublished update for this fund. Continue?'
          : 'This will permanently remove every unpublished update and discard any unpublished changes for this fund. Continue?'
      }
      actions={
        <>
          <StyledButton
            noTopSpacing
            buttonVariant="inactiveOrCancel"
            sx={SECONDARY_BUTTON_HOVER_SX}
            onClick={onClose}
          >
            Cancel
          </StyledButton>
          <StyledButton noTopSpacing buttonVariant="warning" onClick={confirm}>
            {action === 'publish' ? 'Publish All' : 'Revert To Published'}
          </StyledButton>
        </>
      }
    >
      <></>
    </StyledModal>
  );
}

export default function FundUpdate(props: FundUpdateProps) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentInvestor, setCurrentInvestor] = useState<Investor | undefined>(props.investor);
  const [toolbarState, setToolbarState] = useState<FundManagementToolbarState | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState<'publish' | 'revert' | null>(null);
  const fallbackToolbarRef = useRef<HTMLDivElement>(null);
  const fundUpdateBaseRef = useRef<InstanceType<typeof FundUpdateBase>>(null);
  const triggerRefresh = () => setRefreshKey((prev) => prev + 1);
  const view = usePageLevelPreviewMode(toolbarState);

  // Arriving via an in-app link (rather than a fresh tab) keeps the browser's
  // scroll position from whatever page was open before — land at the top of
  // this one instead.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fundUpdateBase = (
    <FundUpdateBase
      ref={fundUpdateBaseRef}
      {...props}
      canEdit={true}
      parentTone="admin"
      backendService={() => new BackendService()}
      renderAdminEditPanel={(panelProps) => (
        <AdminEditPanel
          {...panelProps}
          fundOpportunity={props.fundOpportunity}
          onToolbarStateChange={setToolbarState}
        />
      )}
      prependSection={
        <AdminPrependSection
          investor={currentInvestor}
          fundOpportunity={props.fundOpportunity}
          onDocumentsAdded={triggerRefresh}
          onInvestorUpdate={setCurrentInvestor}
        />
      }
      appendSection={
        <AdminAppendSection investor={currentInvestor} fundOpportunity={props.fundOpportunity} />
      }
      renderCashflowReportsSection={() => (
        <CashflowReportsSection
          fundOpportunity={props.fundOpportunity}
          onReportsChanged={() => fundUpdateBaseRef.current?.loadIRRUpdates()}
        />
      )}
      key={refreshKey}
    />
  );

  return (
    <>
      <FundManagementToolbar
        fundOpportunity={props.fundOpportunity}
        toolbarState={toolbarState}
        fallbackToolbarRef={fallbackToolbarRef}
        onBulkConfirm={setBulkConfirm}
        pageMode={view.pageMode}
        onPageModeChange={view.setPageMode}
        pageLevelPreview={view.isPageLevelPreview}
      />
      <FundManagementBody view={view} fundOpportunity={props.fundOpportunity}>
        {fundUpdateBase}
      </FundManagementBody>
      <BulkConfirmModal
        action={bulkConfirm}
        toolbarState={toolbarState}
        onClose={() => setBulkConfirm(null)}
      />
    </>
  );
}
